export const CONTENT_CACHE_SCHEMA_VERSION = 1;
export const CONTENT_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const CONTENT_CACHE_MAX_ENTRIES = 200;
export const CONTENT_CACHE_MAX_BYTES = 25 * 1024 * 1024;

const DATABASE_NAME = "akedo-content-cache";
const DATABASE_VERSION = 1;
const STORE_NAME = "entries";
const CACHE_KEY_PREFIX = `akedo-content:v${CONTENT_CACHE_SCHEMA_VERSION}`;

const memoryEntries = new Map();
const inFlightRequests = new Map();
let databasePromise = null;
let prunePromise = null;
let pruneRequested = false;

const blockedPersistentFields = new Set([
	"access_token",
	"accesstoken",
	"balance",
	"dkey",
	"download",
	"encryptedpassword",
	"message",
	"messages",
	"password",
	"token",
	"user",
	"usermessages",
]);

const normalizeFieldName = (field) => String(field).replace(/[^a-z0-9]/gi, "").toLowerCase();

const stableValue = (value) => {
	if (Array.isArray(value)) {
		return value.map(stableValue);
	}
	if (value && typeof value === "object") {
		return Object.keys(value)
			.sort()
			.reduce((result, key) => {
				const nextValue = value[key];
				if (nextValue !== undefined) {
					result[key] = stableValue(nextValue);
				}
				return result;
			}, {});
	}
	return value;
};

export const stableSerialize = (value) => JSON.stringify(stableValue(value));

export const sanitizeContentPayload = (value, seen = new WeakSet()) => {
	if (Array.isArray(value)) {
		return value.map((item) => sanitizeContentPayload(item, seen));
	}
	if (!value || typeof value !== "object") {
		return value;
	}
	if (seen.has(value)) {
		return undefined;
	}

	seen.add(value);
	const sanitized = {};
	Object.entries(value).forEach(([key, item]) => {
		if (blockedPersistentFields.has(normalizeFieldName(key))) {
			return;
		}
		const sanitizedItem = sanitizeContentPayload(item, seen);
		if (sanitizedItem !== undefined) {
			sanitized[key] = sanitizedItem;
		}
	});
	seen.delete(value);
	return sanitized;
};

export const getContentPlatformScope = () => {
	if (typeof navigator === "undefined") {
		return "unknown";
	}
	const platform = navigator.userAgentData?.platform || navigator.platform || "unknown";
	const mobile = navigator.userAgentData?.mobile
		?? (typeof window !== "undefined" && window.innerWidth <= 768);
	return `${String(platform).toLowerCase()}:${mobile ? "mobile" : "desktop"}`;
};

export const buildContentCacheKey = ({
	resource,
	params = {},
	userScope = "guest",
	platformScope = getContentPlatformScope(),
}) => [
	CACHE_KEY_PREFIX,
	encodeURIComponent(String(userScope || "guest")),
	encodeURIComponent(String(platformScope || "unknown")),
	encodeURIComponent(String(resource)),
	encodeURIComponent(stableSerialize(params)),
].join(":");

const estimateBytes = (entry) => {
	try {
		return new Blob([JSON.stringify(entry)]).size;
	} catch {
		return JSON.stringify(entry).length * 2;
	}
};

const openDatabase = () => {
	if (databasePromise) {
		return databasePromise;
	}
	if (typeof indexedDB === "undefined") {
		return Promise.resolve(null);
	}

	databasePromise = new Promise((resolve) => {
		let request;
		try {
			request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
		} catch {
			resolve(null);
			return;
		}
		request.onupgradeneeded = () => {
			if (!request.result.objectStoreNames.contains(STORE_NAME)) {
				request.result.createObjectStore(STORE_NAME, { keyPath: "key" });
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => resolve(null);
		request.onblocked = () => resolve(null);
	});

	return databasePromise;
};

const runTransaction = async (mode, operation) => {
	const database = await openDatabase();
	if (!database) {
		return null;
	}

	return new Promise((resolve) => {
		let transaction;
		try {
			transaction = database.transaction(STORE_NAME, mode);
			const request = operation(transaction.objectStore(STORE_NAME));
			request.onsuccess = () => resolve(request.result);
			request.onerror = () => resolve(null);
			transaction.onabort = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
};

const readPersistentEntry = (key) => runTransaction("readonly", (store) => store.get(key));
const readAllPersistentEntries = () => runTransaction("readonly", (store) => store.getAll());
const writePersistentEntry = (entry) => runTransaction("readwrite", (store) => store.put(entry));
const deletePersistentEntry = (key) => runTransaction("readwrite", (store) => store.delete(key));

const isUsableEntry = (entry, now = Date.now()) => (
	entry
	&& entry.schemaVersion === CONTENT_CACHE_SCHEMA_VERSION
	&& Number(entry.cachedAt) > 0
	&& now - Number(entry.cachedAt) < CONTENT_CACHE_TTL_MS
);

const removeEntry = async (key) => {
	memoryEntries.delete(key);
	await deletePersistentEntry(key);
};

const prune = async () => {
	const persistentEntries = await readAllPersistentEntries();
	const combined = new Map(memoryEntries);
	if (Array.isArray(persistentEntries)) {
		persistentEntries.forEach((entry) => {
			if (entry?.key) {
				combined.set(entry.key, entry);
			}
		});
	}

	const now = Date.now();
	const entries = Array.from(combined.values());
	const expired = entries.filter((entry) => !isUsableEntry(entry, now));
	await Promise.all(expired.map((entry) => removeEntry(entry.key)));

	const usable = entries
		.filter((entry) => isUsableEntry(entry, now))
		.sort((left, right) => Number(right.lastAccessedAt) - Number(left.lastAccessedAt));
	let totalBytes = usable.reduce((sum, entry) => sum + Number(entry.sizeBytes || estimateBytes(entry)), 0);
	const removals = [];
	while (usable.length > CONTENT_CACHE_MAX_ENTRIES || totalBytes > CONTENT_CACHE_MAX_BYTES) {
		const entry = usable.pop();
		removals.push(entry);
		totalBytes -= Number(entry.sizeBytes || estimateBytes(entry));
	}
	await Promise.all(removals.map((entry) => removeEntry(entry.key)));
};

const schedulePrune = () => {
	pruneRequested = true;
	if (!prunePromise) {
		prunePromise = (async () => {
			while (pruneRequested) {
				pruneRequested = false;
				await prune();
			}
		})().finally(() => {
			prunePromise = null;
		});
	}
	return prunePromise;
};

export const contentCacheRepository = {
	async get(key) {
		let entry = memoryEntries.get(key);
		if (!entry) {
			entry = await readPersistentEntry(key);
		}
		if (!isUsableEntry(entry)) {
			if (entry?.key) {
				await removeEntry(entry.key);
			}
			return null;
		}

		const accessedEntry = { ...entry, lastAccessedAt: Date.now() };
		memoryEntries.set(key, accessedEntry);
		void writePersistentEntry(accessedEntry);
		return accessedEntry;
	},

	async set({ key, resource, params, userScope, platformScope, payload }) {
		const now = Date.now();
		const entry = {
			key,
			resource,
			params: stableValue(params),
			userScope: String(userScope || "guest"),
			platformScope: String(platformScope || "unknown"),
			payload: sanitizeContentPayload(payload),
			schemaVersion: CONTENT_CACHE_SCHEMA_VERSION,
			cachedAt: now,
			lastAccessedAt: now,
		};
		entry.sizeBytes = estimateBytes(entry);
		memoryEntries.set(key, entry);
		await writePersistentEntry(entry);
		void schedulePrune();
		return entry;
	},

	remove: removeEntry,

	async invalidate({ resource, userScope, keyPrefix, predicate } = {}) {
		const persistentEntries = await readAllPersistentEntries();
		const combined = new Map(memoryEntries);
		if (Array.isArray(persistentEntries)) {
			persistentEntries.forEach((entry) => entry?.key && combined.set(entry.key, entry));
		}

		const matches = (entry) => (
			(!resource || entry.resource === resource)
			&& (!userScope || entry.userScope === String(userScope))
			&& (!keyPrefix || entry.key.startsWith(keyPrefix))
			&& (!predicate || predicate(entry))
		);
		await Promise.all(Array.from(combined.values()).filter(matches).map((entry) => removeEntry(entry.key)));
	},

	clearUserScope(userScope) {
		return this.invalidate({ userScope: String(userScope || "guest") });
	},

	runDeduplicated(key, requestFactory) {
		if (inFlightRequests.has(key)) {
			return inFlightRequests.get(key);
		}
		const request = Promise.resolve()
			.then(requestFactory)
			.finally(() => inFlightRequests.delete(key));
		inFlightRequests.set(key, request);
		return request;
	},
};

export const areContentPayloadsEqual = (left, right) => (
	stableSerialize(sanitizeContentPayload(left)) === stableSerialize(sanitizeContentPayload(right))
);

export const __resetContentCacheForTests = async () => {
	memoryEntries.clear();
	inFlightRequests.clear();
	if (prunePromise) {
		await prunePromise;
	}
	prunePromise = null;
	pruneRequested = false;
	const database = databasePromise ? await databasePromise : null;
	database?.close();
	databasePromise = null;
	if (typeof indexedDB !== "undefined") {
		await new Promise((resolve) => {
			const request = indexedDB.deleteDatabase(DATABASE_NAME);
			request.onsuccess = () => resolve();
			request.onerror = () => resolve();
			request.onblocked = () => resolve();
		});
	}
};
